import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Check, Palette, X } from 'lucide-react';
export const Route = createFileRoute('/_layout/compare/flowcode/')({
    component: CompareFlowcodePage,
    head: () => ({
        meta: [
            { title: 'Adora vs Flowcode — Offline Performance Analytics vs QR Branding' },
            {
                name: 'description',
                content: 'Flowcode creates branded QR codes. Adora measures how offline campaigns actually perform over time. Compare both tools and see which one fits your goals.',
            },
            { property: 'og:title', content: 'Adora vs Flowcode — Offline Performance Analytics vs QR Branding' },
            {
                property: 'og:description',
                content: 'Flowcode creates branded QR codes. Adora measures how offline campaigns actually perform over time. Compare both tools and see which one fits your goals.',
            },
        ],
    }),
});
function CompareFlowcodePage() {
    return (<main className="min-h-dvh bg-background">
            {/* Hero */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Compare tools</p>

                <h1 className="mt-2 text-3xl md:text-5xl font-bold">Adora vs Flowcode</h1>

                <p className="mt-4 max-w-2xl text-muted-foreground">
                    Both Adora and Flowcode connect physical touchpoints to digital destinations — but they solve very
                    different problems.
                </p>

                <p className="mt-3 max-w-2xl text-muted-foreground">
                    <strong>TL;DR:</strong> Flowcode focuses on branded QR codes. Adora focuses on measuring and
                    improving offline campaign performance.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg">
                        <Link to="/sign-up" search={{ redirect: '/console' }}>
                            Try Adora for free
                        </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                        <Link to="/what-is-adora">What is Adora?</Link>
                    </Button>
                </div>
            </section>

            <Separator />

            {/* Core positioning */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <div className="grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Palette className="h-5 w-5 text-primary"/>
                                Flowcode positioning
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>
                                Flowcode is built around <strong>QR codes as branded entry points</strong>. It shines
                                when design, aesthetics, and fast access matter most.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li>• Event campaigns</li>
                                <li>• Brand activations</li>
                                <li>• Short-term promotions</li>
                            </ul>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5 text-primary"/>
                                Adora positioning
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>
                                Adora is built around <strong>offline campaign analytics</strong>. It treats QR codes
                                and links as data sources you can compare and optimize.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li>• Flyers, menus, packaging</li>
                                <li>• Ongoing offline distribution</li>
                                <li>• Performance-driven decisions</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Feature comparison */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">Feature comparison</h2>

                <div className="mt-6 overflow-x-auto">
                    <table className="w-full border text-sm">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-4 py-3 text-left">Capability</th>
                                <th className="px-4 py-3 text-left">Adora</th>
                                <th className="px-4 py-3 text-left">Flowcode</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            <Row label="Branded QR codes" adora flowcode/>
                            <Row label="Offline campaign tracking" adora/>
                            <Row label="Campaign-level analytics" adora/>
                            <Row label="Compare placements & formats" adora/>
                            <Row label="One-off event focus" flowcode/>
                            <Row label="Long-term offline performance" adora/>
                            <Row label="Design-first QR experience" flowcode/>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Decision helper */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">Which one should you choose?</h2>

                <div className="mt-6 grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Choose Flowcode if…</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• You run short-term campaigns or events</p>
                            <p>• Branding and visual QR design are top priority</p>
                            <p>• You don’t need deep performance analytics</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Choose Adora if…</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• Your marketing exists offline</p>
                            <p>• You want to know which placements work best</p>
                            <p>• You optimize campaigns over time</p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12">
                <Card>
                    <CardContent className="p-6 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <h3 className="text-xl md:text-2xl font-semibold">
                                Built for understanding offline performance
                            </h3>
                            <p className="mt-2 text-muted-foreground max-w-xl text-sm md:text-base">
                                If QR codes are only the entry point — not the goal — Adora gives you insight Flowcode
                                was not designed to provide.
                            </p>
                        </div>
                        <Button asChild size="lg">
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                Start with Adora
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            </section>
        </main>);
}
function Row({ label, adora, flowcode }) {
    return (<tr>
            <td className="px-4 py-3">{label}</td>
            <td className="px-4 py-3">
                {adora ? <Check className="h-4 w-4 text-green-600"/> : <X className="h-4 w-4 text-muted-foreground"/>}
            </td>
            <td className="px-4 py-3">
                {flowcode ? (<Check className="h-4 w-4 text-green-600"/>) : (<X className="h-4 w-4 text-muted-foreground"/>)}
            </td>
        </tr>);
}
